import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BoatService from '../services/BoatService';
import './MobileControls.css';

const MobileControls = ({ boatId, onBoatIdChange, onStartTracking }) => {
  const [gpsStatus, setGpsStatus] = useState('unknown'); // 'unknown', 'available', 'error'
  const [gpsErrorMessage, setGpsErrorMessage] = useState('');
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [showGpsInfo, setShowGpsInfo] = useState(false);
  const [assignedBoats, setAssignedBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get authentication context
  const { user, token } = useAuth();

  // Check GPS status and fetch assigned boats on component mount
  useEffect(() => {
    checkGpsStatus();
    if (user && token) {
      fetchAssignedBoats();
    }
  }, [user, token]);

  const fetchAssignedBoats = async () => {
    setLoading(true);
    try {
      // If user doesn't have any assigned boats, return empty array
      if (!user || !user.assignedBoats || user.assignedBoats.length === 0) {
        setAssignedBoats([]);
        setLoading(false);
        return;
      }

      const data = await BoatService.getBoatsRegistry(token);
      
      // Filter boats that are assigned to the user
      const userBoats = data.filter(boat => 
        user.assignedBoats.includes(boat.boatId)
      );
      
      setAssignedBoats(userBoats);
      setError(null);
      
      // If we have assigned boats and no boat selected yet, select the first one
      if (userBoats.length > 0 && !boatId) {
        onBoatIdChange(userBoats[0].boatId);
      }
    } catch (err) {
      setError('Failed to load assigned boats: ' + err.message);
      console.error("Error fetching assigned boats:", err);
    } finally {
      setLoading(false);
    }
  };

  const checkGpsStatus = () => {
    if (!navigator.geolocation) {
      setGpsStatus('error');
      setGpsErrorMessage('आपके ब्राउज़र में जियोलोकेशन सपोर्ट नहीं है। Geolocation is not supported in your browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsStatus('available');
        setGpsCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setGpsAccuracy(position.coords.accuracy);
      },
      (error) => {
        setGpsStatus('error');
        
        // Translate the error message
        let errorMsg = '';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'लोकेशन एक्सेस की अनुमति नहीं है। Location access permission denied.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'लोकेशन जानकारी उपलब्ध नहीं है। Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMsg = 'लोकेशन अनुरोध टाइमआउट हो गया। Location request timed out.';
            break;
          default:
            errorMsg = 'अज्ञात त्रुटि हुई। Unknown error occurred.';
        }
        setGpsErrorMessage(errorMsg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const openGpsSettings = () => {
    if (gpsStatus === 'error') {
      if (/android/i.test(navigator.userAgent)) {
        // For Android devices - open location settings
        if (confirm('GPS सेटिंग्स खोलें? Open GPS settings?')) {
          window.location.href = 'intent://settings/location#Intent;scheme=android-app;end';
        }
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // For iOS devices - guide user to settings
        alert('कृपया अपने डिवाइस सेटिंग्स में जाकर इस ब्राउज़र के लिए लोकेशन अनुमति दें। Please go to your device Settings > Privacy > Location Services to enable location for this browser.');
      } else {
        // For other devices
        alert('कृपया अपने ब्राउज़र सेटिंग्स में जाकर लोकेशन अनुमति दें। Please check your browser settings to enable location services.');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check GPS before starting
    if (gpsStatus !== 'available') {
      checkGpsStatus();
      if (gpsStatus !== 'available') {
        alert('कृपया पहले GPS कनेक्शन सुनिश्चित करें। Please check your GPS connection first.');
        return;
      }
    }
    
    if (boatId.trim()) {
      onStartTracking();
    } else {
      alert('कृपया नाव का चयन करें। Please select a boat.');
    }
  };

  const toggleGpsInfo = () => {
    setShowGpsInfo(!showGpsInfo);
    // If showing GPS info, refresh the status
    if (!showGpsInfo) {
      checkGpsStatus();
    }
  };
  
  // Find the display name of the selected boat
  const getSelectedBoatName = () => {
    const selectedBoat = assignedBoats.find(boat => boat.boatId === boatId);
    return selectedBoat ? selectedBoat.displayName || selectedBoat.boatId : boatId;
  };

  return (
    <div className="mobile-controls">
      <div className="mobile-header">
        <h2>नाव लोकेशन ट्रैकिंग</h2>
        <h3>Boat Location Tracking</h3>
      </div>
      
      <div className={`gps-status ${gpsStatus}`}>
        <div className="gps-indicator" onClick={toggleGpsInfo}>
          <span className="gps-icon"></span>
          {gpsStatus === 'unknown' && 'GPS जाँच हो रही है... Checking GPS...'}
          {gpsStatus === 'available' && 'GPS उपलब्ध है | GPS Available'}
          {gpsStatus === 'error' && 'GPS समस्या | GPS Issue'}
        </div>
        
        {showGpsInfo && (
          <div className="gps-details">
            {gpsStatus === 'available' ? (
              <>
                <p>
                  <strong>GPS अक्षांश/अक्षरेखा | GPS Latitude/Longitude:</strong><br />
                  {gpsCoords.latitude.toFixed(6)}, {gpsCoords.longitude.toFixed(6)}
                </p>
                <p>
                  <strong>GPS सटीकता | GPS Accuracy:</strong><br />
                  {gpsAccuracy ? `${Math.round(gpsAccuracy)} मीटर | meters` : 'अज्ञात | Unknown'}
                </p>
                <button className="refresh-gps" onClick={checkGpsStatus}>
                  रिफ्रेश करें | Refresh GPS
                </button>
              </>
            ) : (
              <>
                <p className="error-message">{gpsErrorMessage}</p>
                <button className="gps-settings-btn" onClick={openGpsSettings}>
                  GPS सेटिंग्स खोलें | Open GPS Settings
                </button>
                <button className="refresh-gps" onClick={checkGpsStatus}>
                  पुन: जाँच करें | Check Again
                </button>
              </>
            )}
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="tracking-form">
        <div className="form-group">
          <label htmlFor="boatId">
            <span>आपकी नाव का चयन करें / Select Your Boat</span>
            <small className="form-hint">
              आपको सौंपी गई नावें यहां दिखाई देंगी
              <br />
              Boats assigned to you will appear here
            </small>
          </label>
          {loading ? (
            <div className="loading-boats">Loading your boats...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : assignedBoats.length === 0 ? (
            <div className="no-assigned-boats">
              <p>आपको कोई नाव असाइन नहीं की गई है</p>
              <p>You don't have any boats assigned to you</p>
              <p>प्रशासक से संपर्क करें | Contact administrator</p>
            </div>
          ) : (
            <div className="boat-selector-container">
              <select
                id="boatId"
                value={boatId}
                onChange={(e) => onBoatIdChange(e.target.value)}
                className="boat-selector-dropdown"
                required
              >
                <option value="">-- नाव चुनें | Select a boat --</option>
                {assignedBoats.map(boat => (
                  <option key={boat.boatId} value={boat.boatId}>
                    {boat.displayName || boat.boatId}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {assignedBoats.length > 0 && (
          <>
            <div className="selected-boat-info">
              <h4>चयनित नाव | Selected boat:</h4>
              <div className="selected-boat-name">{getSelectedBoatName()}</div>
            </div>

            <div className="instructions">
              <p>लोकेशन ट्रैकिंग शुरू करने के लिए बटन दबाएं</p>
              <p>Press the button to start location tracking</p>
            </div>
            
            <button 
              type="submit" 
              className={`start-btn ${gpsStatus !== 'available' || !boatId ? 'disabled' : ''}`}
              disabled={gpsStatus !== 'available' || !boatId}
            >
              शुरू करें | Start Tracking
            </button>
          </>
        )}
      </form>
      
      <div className="note">
        <p>
          <strong>नोट:</strong> ट्रैकिंग शुरू करने के लिए आपको अपने डिवाइस के लिए लोकेशन अनुमति देनी होगी।
        </p>
        <p>
          <strong>Note:</strong> You'll need to allow location permissions for your device to start tracking.
        </p>
      </div>
    </div>
  );
};

export default MobileControls;
 